<template>
  <b-card>
    <div
      v-if="isFetching"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Loading..."
      />
    </div>
    <b-overlay
      v-else
      :show="isLoading"
      rounded="sm"
      bg-color="#000a1e"
      opacity="0.8"
    >
      <b-container class="px-md-5">
        <validation-observer
          ref="formObserver"
          #default="{ invalid }"
        >
          <b-row>
            <b-col md="4">
              <b-form-group
                label="Course"
                label-for="course-select"
              >
                <v-select
                  id="course-select"
                  v-model="selectedCourse"
                  :options="courses"
                  label="name"
                  :reduce="(course) => course.id"
                  placeholder="Select Course"
                  @input="onCourseSelect"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Assignment"
                label-for="activity-code"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Assignment"
                  rules="required"
                >
                  <v-select
                    id="activity-code"
                    v-model="activity_code"
                    :options="assignments"
                    label="name"
                    :reduce="(assignment) => assignment.id"
                    :disabled="!selectedCourse"
                    placeholder="Select Assignment"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Experiments"
                label-for="experiments"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Experiments"
                  rules="required"
                >
                  <v-select
                    id="experiments"
                    v-model="experiment_id"
                    :options="experimentsLookups"
                    label="name"
                    :reduce="(experiment) => experiment.id"
                    placeholder="Select Experiments"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="mt-2">
            <b-col cols="12">
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <h3>Select Students</h3>
                <b-form-input
                  v-model="filter.keyword"
                  placeholder="Search Students..."
                  class="w-25"
                />
              </div>
              <b-table
                ref="studentsTable"
                :items="fetchUsers"
                :fields="fields"
                responsive
                show-empty
                empty-text="No students found"
                selectable
                select-mode="multi"
                @row-selected="onRowSelected"
              >
                <!-- Checkbox Column Header -->
                <template #head(selected)="{ selectAllRows, clearSelected }">
                  <b-form-checkbox
                    :checked="allSelected"
                    @change="toggleSelectAll"
                  />
                </template>

                <!-- Checkbox Column Cell -->
                <template
                  #cell(selected)="{ rowSelected, selectRow, unselectRow }"
                >
                  <b-form-checkbox
                    :checked="rowSelected"
                    @change="rowSelected ? unselectRow() : selectRow()"
                  />
                </template>

                <template #cell(name)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar
                      :src="data.item.avatar"
                      :text="avatarText(data.item.name)"
                      variant="light-primary"
                      class="mr-1"
                    />
                    <div>
                      <b-link
                        v-if="data.item.id"
                        :to="{ name: 'pages_profile', params: { user_id: data.item.id } }"
                        class="font-weight-bold d-block text-primary"
                      >
                        {{ data.item.name }}
                      </b-link>
                      <span
                        v-else
                        class="font-weight-bold d-block"
                      >{{
                        data.item.name
                      }}</span>
                      <!-- <small class="text-muted">{{ data.item.email }}</small> -->
                    </div>
                  </div>
                </template>
              </b-table>

              <!-- Pagination & Per Page -->
              <b-row class="mt-2 w-100 align-items-center">
                <b-col md="4" class="text-light d-flex align-items-center" style="font-size: 0.95rem;">
                  <span v-if="totalUsers > 0" class="d-flex align-items-center" style="margin-left: 2px;">
                    Showing 
                    <b-form-select
                      v-model="perPage"
                      :options="[10, 25, 50, 100]"
                      class="custom-per-page mx-1"
                      @change="currentPage = 1; $refs.studentsTable.refresh()"
                    />
                  </span>
                </b-col>
                <b-col md="4" class="d-flex justify-content-center">
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalUsers"
                    :per-page="perPage"
                    class="mb-0"
                  />
                </b-col>
                <b-col md="4"></b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row class="mt-2">
            <b-col
              cols="12"
              class="text-right"
            >
              <b-button
                variant="primary"
                :disabled="isLoading"
                @click="submitActivity"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-1"
                />
                {{ isEditMode ? "Update Activity" : "Create Activity" }}
              </b-button>
            </b-col>
          </b-row>
        </validation-observer>
      </b-container>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BContainer,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BAvatar,
  BPagination,
  BFormCheckbox,
  BSpinner,
  BOverlay,
  BFormSelect,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import store from '@/store'
import { avatarText } from '@core/utils/filter'
import { onUnmounted } from '@vue/composition-api'
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'

export default {
  name: 'CreateActivity',
  components: {
    BCard,
    BRow,
    BCol,
    BContainer,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BAvatar,
    BPagination,
    BFormCheckbox,
    BSpinner,
    BOverlay,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormSelect,
  },
  data() {
    return {
      required,
      activity_code: '',
      courses: [],
      assignments: [],
      selectedCourse: null,
      experiment_ids: null,
      experiment_id: null,
      selectedStudents: [],
      filter: {
        keyword: '',
        institution_id: null,
      },
      currentPage: 1,
      perPage: 10,
      totalUsers: 0,
      isLoading: false,
      isFetching: false,
      allSelected: false,
      fields: [
        { key: 'selected', label: '', thStyle: { width: '50px' } },
        { key: 'name', label: 'Student' },
        { key: 'email', label: 'Email' },
        { key: 'institution', label: 'Institution' },
      ],
      isEditMode: false,
      activityId: null,
      initialActivityUsers: [],
    }
  },
  computed: {
    ...mapGetters({
      experimentsLookups: 'experiments/experimentsLookups',
    }),
  },
  watch: {
    'filter.keyword': function () {
      this.$refs.studentsTable.refresh()
    },
    currentPage() {
      this.$refs.studentsTable.refresh()
    },
  },
  created() {
    // Fetch experiments lookups if not already available
    if (this.experimentsLookups.length === 0) {
      this.$store.dispatch('experiments/experimentsLookups')
    }

    // Check for edit mode
    if (this.$route.name === 'edit-activity') {
      this.isEditMode = true
      this.activityId = this.$route.params.id
      this.fetchActivityDetails()
    } else if (this.$route.params.id) {
      // Create mode with institution ID
      this.fetchLMSCourses(this.$route.params.id)
    }
  },
  methods: {
    avatarText,
    fetchLMSCourses(institutionId) {
      if (!institutionId) return Promise.resolve()
      return this.$store
        .dispatch('activities/LMSCoursesLookups', institutionId)
        .then(response => {
          console.log('LMSCoursesLookups response:', response)
          this.courses = response.data
        })
        .catch(error => {
          console.error(error)
        })
    },
    onCourseSelect(courseId) {
      this.assignments = []
      this.activity_code = ''
      if (courseId) {
        this.fetchLMSAssignments(courseId)
      }
    },
    fetchLMSAssignments(courseId) {
      const institutionId = this.filter.institution_id || this.$route.params.id // Use filter.institution_id which covers both edit/create cases ideally
      if (!institutionId) return Promise.resolve()

      return this.$store
        .dispatch('activities/LMSAssignmentsLookups', {
          id: institutionId,
          course_id: { course_id: courseId },
        })
        .then(response => {
          this.assignments = response.data.assignments
          console.log('Assignments loaded:', this.assignments)
        })
        .catch(error => {
          console.error(error)
        })
    },
    fetchActivityDetails() {
      this.isFetching = true
      this.$store
        .dispatch('activities/get', this.activityId)
        .then(response => {
          const activity = response.data
          console.log('fetchActivityDetails activity:', activity)
          // Ensure activity_code matches the type of assignment IDs (likely numbers)
          this.activity_code = isNaN(Number(activity.activity_code))
            ? activity.activity_code
            : Number(activity.activity_code)

          // Handle experiment_id
          let exp = activity.experiment_id
          if (Array.isArray(exp)) {
            exp = exp[0]
          }
          this.experiment_id = exp && typeof exp === 'object' ? exp.id : exp

          if (activity.users) {
            this.selectedStudents = activity.users
            // Store initial users to ensure they are re-selected after table refresh
            this.initialActivityUsers = activity.users
          }

          if (activity.moodle_course_id) {
            this.selectedCourse = activity.moodle_course_id
            console.log('Setting selectedCourse:', this.selectedCourse)
          }

          const promises = []

          // If activity has institution_id, set it for filter
          if (activity.institution_id) {
            this.filter.institution_id = activity.institution_id
            promises.push(this.fetchLMSCourses(activity.institution_id))

            if (this.selectedCourse) {
              promises.push(this.fetchLMSAssignments(this.selectedCourse))
            }
          }

          // Refresh table to ensure users are loaded and selection is applied
          this.$nextTick(() => {
            this.$refs.studentsTable.refresh()
          })

          return Promise.all(promises)
        })
        .catch(error => {
          console.error(error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching activity details',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchUsers(ctx, callback) {
      // For create mode, use route param as institution_id if available
      if (!this.isEditMode && this.$route.params.id) {
        this.filter.institution_id = this.$route.params.id
      }

      this.$store
        .dispatch('users/users', {
          page: this.currentPage,
          per_page: this.perPage,
          ...this.filter,
        })
        .then(response => {
          const users = response.data.map(user => ({
            id: user.id,
            name: `${user.fname} ${user.lname}`,
            email: user.email,
            institution: user.institution ? user.institution.name : 'N/A',
            avatar: user.avatar,
            username: user.username,
          }))
          const total = response.meta ? response.meta.total : users.length
          this.totalUsers = total
          callback(users)

          // Apply selection
          this.$nextTick(() => {
            users.forEach((user, index) => {
              // Check if user is in selectedStudents or initialActivityUsers by ID
              const isSelected = this.selectedStudents.some(u => u.id === user.id)
                || (this.initialActivityUsers
                  && this.initialActivityUsers.some(u => u.id === user.id))

              if (isSelected) {
                this.$refs.studentsTable.selectRow(index)
              }
            })
          })

          // Force update selectedStudents if it was cleared but we have initial users matches
          // (This happens via selectRow triggering onRowSelected, so it might be automatic)
        })
        .catch(error => {
          console.error(error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching users',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    onRowSelected(items) {
      this.selectedStudents = items
      this.allSelected = items.length === this.totalUsers && this.totalUsers > 0
    },
    toggleSelectAll(checked) {
      if (checked) {
        this.$refs.studentsTable.selectAllRows()
      } else {
        this.$refs.studentsTable.clearSelected()
      }
    },
    submitActivity() {
      this.$refs.formObserver.validate().then(success => {
        if (success) {
          if (this.selectedStudents.length === 0) {
            this.$swal({
              icon: 'warning',
              title: 'Missing Information',
              text: 'Please select at least one student.',
            })
            return
          }

          this.isLoading = true

          const payload = this.isEditMode
            ? {
              activity_code: this.activity_code,
              experiment_id: this.experiment_id,
              moodle_course_id: this.selectedCourse,
              user_ids: this.selectedStudents.map(u => u.id),
            }
            : {
              activity_code: this.activity_code,
              institution_id: this.$route.params.id,
              experiment_id: this.experiment_id,
              moodle_course_id: this.selectedCourse,
              user_ids: this.selectedStudents.map(u => u.id),
            }

          const action = this.isEditMode
            ? 'activities/update'
            : 'activities/store'
          const dispatchPayload = this.isEditMode
            ? { id: this.activityId, data: payload }
            : payload
          console.log('dispatchPayload', dispatchPayload)

          this.$store
            .dispatch(action, dispatchPayload)
            .then(response => {
              this.$swal({
                icon: 'success',
                title: 'Success',
                text: `Activity ${
                  this.isEditMode ? 'updated' : 'created'
                } successfully!`,
              })
              this.$router.push({
                name: 'institution_profile',
                params: { id: this.filter.institution_id },
                query: { tab: 'lms-config' },
              })
              if (!this.isEditMode) {
                // Reset form only on create
                this.activity_code = ''
                this.experiment_ids = []
                this.$refs.studentsTable.clearSelected()
                this.$nextTick(() => {
                  this.$refs.formObserver.reset()
                })
              } else {
                // Navigate back or stay? Usually stay or go back to list.
                // this.$router.push({ name: 'lms-activities' }); // Assuming route name
              }
              this.isLoading = false
            })
            .catch(error => {
              console.error(error)
              this.isLoading = false
              if (error.response) {
                this.$swal({
                  icon: 'error',
                  title: 'Error',
                  text:
                    error.response.data.message
                    || error.response.data.error
                    || `Failed to ${
                      this.isEditMode ? 'update' : 'create'
                    } activity.`,
                })
              }
            })
        } else {
          this.$swal({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please fill in all required fields.',
          })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.v-select.vs--disabled {
  .vs__dropdown-toggle,
  .vs__dropdown-toggle:hover {
    background-color: var(--secondary-bg, #161d31) !important;
    border-color: #3b4253 !important;
    cursor: not-allowed;
  }

  .vs__selected,
  .vs__search {
    background-color: transparent !important;
  }

  .vs__actions,
  .vs__actions svg,
  .vs__open-indicator {
    background-color: transparent !important;
    color: #b4b7bd !important;
    fill: #b4b7bd !important;
  }
}
</style>
